import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import type { PostgrestError } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { resolveTimezone, shouldBackfillTimezone } from "@/lib/deliveryChannel";

export interface UserPrefs {
  mom_types: string[] | null;
  zip_code: string | null;
  proactiveness_level: number;
  onboarding_complete: boolean;
  favorite_chips: string[];
  known_kid_names: string[];
  known_kid_colors: Record<string, string>;
  /** #1243 — names removed (or renamed away from) in My Kids, so kin-chat can
   *  be told not to treat them as this family's children. Raw jsonb; parse with
   *  `parseLedger` from `@/lib/removedKidLedger`. Never index into it — entries
   *  are addressed by name. */
  removed_kids: unknown;
  /** Pro Setup focus-areas card (#361). Stable keys, never display labels.
   *  null = never asked; consumers fall back to their own default order. */
  focus_areas: string[] | null;
  notifications_weekly_enabled: boolean;
  notifications_weekly_day_of_week: number;
  notifications_weekly_time: string;
  notifications_morning_enabled: boolean;
  notifications_morning_time: string;
  notifications_prompted_at: string | null;
  notifications_timezone: string | null;
  /** #555 — where notifications_timezone came from. 'device' is a guess we
   *  made from the browser clock; 'user' is her deliberate choice; null means
   *  we genuinely do not know (every row predating this column). */
  notifications_timezone_source: "device" | "user" | null;
  my_kin_section_order: unknown;
  briefing_prefs: unknown;
  phone_number: string | null;
  phone_verified_at: string | null;
  sms_morning_enabled: boolean;
  sms_task_updates_enabled: boolean;
  sms_weekly_enabled: boolean;
  notifications_task_updates_enabled: boolean;
  /** #109 — the "Ready for you" row: Kin Inbox parses and Beauty Analyzer
   *  renders, which used to ride on the task-updates pair. Columns are
   *  BACKFILLED from that pair, so before the migration runs "absent" has to
   *  read as her task-updates value, not as a fresh default. */
  notifications_ready_enabled: boolean;
  sms_ready_enabled: boolean;
  sms_opted_out: boolean;
  /** #1141 — whether inbound texts route to kin-chat. Absent before the migration; absent means ON. */
  sms_two_way_enabled: boolean;
  // #743 — My Kin grocery list toggle. Column is NOT NULL DEFAULT true.
  grocery_autofill_enabled: boolean;
  // #742 — overnight Kroger cart auto-load. Column is NOT NULL DEFAULT
  // false — opt-in only, no exceptions (unlike grocery_autofill_enabled,
  // this is a real cart mutation, not an internal list).
  kroger_cart_autoload_enabled: boolean;
}

const DEFAULT_PREFS: UserPrefs = {
  mom_types: null,
  zip_code: null,
  proactiveness_level: 5,
  onboarding_complete: false,
  favorite_chips: [],
  known_kid_names: [],
  known_kid_colors: {},
  focus_areas: null,
  notifications_weekly_enabled: false,
  notifications_weekly_day_of_week: 0,
  notifications_weekly_time: "18:00",
  notifications_morning_enabled: false,
  notifications_morning_time: "07:00",
  notifications_prompted_at: null,
  notifications_timezone: null,
  notifications_timezone_source: null,
  my_kin_section_order: null,
  briefing_prefs: null,
  phone_number: null,
  phone_verified_at: null,
  sms_morning_enabled: false,
  sms_task_updates_enabled: false,
  sms_weekly_enabled: false,
  // Mirrors the column default. Task-update push was ungated before this column
  // existed, so "no row yet" has to mean ON — otherwise a user whose preferences
  // row hasn't loaded reads as having push switched off.
  notifications_task_updates_enabled: true,
  // #109. Mirrors notifications_task_updates_enabled above and for the same
  // reason: Kin Inbox and Beauty Analyzer push was ungated before this column,
  // then rode on the task-updates column, so "no row yet" must read as ON.
  notifications_ready_enabled: true,
  // Text, so opt-in only — same lean as every other sms_* default here.
  sms_ready_enabled: false,
  sms_opted_out: false,
  // ⚠️ Defaults ON, matching the column. Two-way SMS was already live for every
  // verified opted-in Pro user when #1141 added the switch, so OFF would have
  // read as "everyone has it turned off" for the whole pre-SQL window.
  sms_two_way_enabled: true,
  grocery_autofill_enabled: true,
  // Fail closed for a real cart mutation — a not-yet-loaded row (or a
  // pre-migration column-not-found fallback) must never read as opted in.
  kroger_cart_autoload_enabled: false,
  // #1243 — an empty ledger, not null. "Has she removed anyone" must not be a
  // three-valued question in the one feature that must not get it wrong, and a
  // row that hasn't loaded has to read as "nobody removed" rather than unknown.
  removed_kids: [],
};

// Every column except the newest ones, whose migrations may not have run yet —
// grocery_autofill_enabled (#743), kroger_cart_autoload_enabled (#742),
// notifications_timezone_source (#555), removed_kids (#1243),
// sms_two_way_enabled (#1141) and the ready pair (#109). See the
// deploy-order safety net in the fetch effect below for why this needs to be a
// fallback select, not just the primary one.
//
// ⚠️ #1243's removed_kids belongs in the PRIMARY select only, never here. Code
//    deploys on merge and the SQL is hand-run, so between the two this column
//    does not exist. Naming it in the fallback would 400 the preferences read
//    for every user until the migration lands — the fallback is the thing that
//    is supposed to survive that window.
const PREFS_SELECT_COLUMNS_LEGACY =
  "mom_types, zip_code, proactiveness_level, onboarding_complete, favorite_chips, known_kid_names, known_kid_colors, focus_areas, notifications_weekly_enabled, notifications_weekly_day_of_week, notifications_weekly_time, notifications_morning_enabled, notifications_morning_time, notifications_prompted_at, notifications_timezone, my_kin_section_order, briefing_prefs, phone_number, phone_verified_at, sms_morning_enabled, sms_task_updates_enabled, sms_weekly_enabled, notifications_task_updates_enabled, sms_opted_out";

interface UserPreferencesContextType {
  prefs: UserPrefs;
  loading: boolean;
  // Optimistically updates local context state AND writes to Supabase.
  // Pass { write: false } to update only local state (when the caller already
  // handled the DB write and just wants the context in sync).
  //
  // Resolves to the PostgrestError when the write failed, null otherwise (and
  // null for a { write: false } local-only update). supabase-js resolves
  // { data, error } instead of throwing on a DB/RLS failure, so a try/catch
  // around this call only ever sees network-level rejections — a caller that
  // needs to know the row actually landed must check the returned value.
  // Issue #416.
  updatePreferences: (
    patch: Partial<UserPrefs>,
    options?: { write?: boolean }
  ) => Promise<PostgrestError | null>;
  // Re-fetches the full row from Supabase (escape hatch after external writes).
  refetchPreferences: () => void;
}

const UserPreferencesContext = createContext<UserPreferencesContextType>({
  prefs: DEFAULT_PREFS,
  loading: true,
  updatePreferences: async () => null,
  refetchPreferences: () => {},
});

export const UserPreferencesProvider = ({ children }: { children: ReactNode }) => {
  const { user, loading: authLoading } = useAuth();
  const [prefs, setPrefs] = useState<UserPrefs>(DEFAULT_PREFS);
  const [loading, setLoading] = useState(true);
  const [refetchKey, setRefetchKey] = useState(0);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setPrefs(DEFAULT_PREFS);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      let { data, error } = await supabase
        .from("user_preferences")
        .select(
          `${PREFS_SELECT_COLUMNS_LEGACY}, grocery_autofill_enabled, kroger_cart_autoload_enabled, notifications_timezone_source, removed_kids, sms_two_way_enabled, notifications_ready_enabled, sms_ready_enabled` as any
        )
        .eq("user_id", user.id)
        .maybeSingle();

      // ── Deploy-order safety net (#743, same pattern as run-scheduled-tasks'
      // #427/#741 one) ─────────────────────────────────────────────────────
      // grocery_autofill_enabled (#743), kroger_cart_autoload_enabled (#742)
      // and notifications_timezone_source (#555) are columns this frontend
      // starts requesting the moment their PR merges, while the ALTER TABLE is
      // run by hand after (Track 3). In that
      // window PostgREST 400s the WHOLE select for one unrecognized column —
      // which without this retry would null out EVERY field below, including
      // onboarding_complete, silently bouncing a real user back into
      // onboarding on every load. Retry without them; each field still
      // resolves correctly via its own default below.
      if (error) {
        console.error(`[Prefs] select failed (${error.message}), retrying without the newest columns`);
        ({ data, error } = await supabase
          .from("user_preferences")
          .select(PREFS_SELECT_COLUMNS_LEGACY as any)
          .eq("user_id", user.id)
          .maybeSingle());
        if (error) console.error(`[Prefs] retry also failed: ${error.message}`);
      }
      if (cancelled) return;
      const d = data as any;
      setPrefs({
        mom_types: d?.mom_types ?? null,
        zip_code: d?.zip_code ?? null,
        proactiveness_level:
          typeof d?.proactiveness_level === "number" ? d.proactiveness_level : 5,
        onboarding_complete: d?.onboarding_complete ?? false,
        favorite_chips: Array.isArray(d?.favorite_chips) ? d.favorite_chips : [],
        known_kid_names: Array.isArray(d?.known_kid_names) ? d.known_kid_names : [],
        // #1243 — [] when absent, which covers BOTH the legacy-select retry
        // above (this column is not in it, on purpose) and any row written
        // before the migration ran. An empty ledger is the fail-open direction
        // and it is the right one here: the alternative is Kin refusing to
        // discuss a child who was never removed.
        removed_kids: Array.isArray(d?.removed_kids) ? d.removed_kids : [],
        // null, not [] — "never asked" and "asked, picked nothing" are
        // different, and only the first should fall back to a default order.
        focus_areas: Array.isArray(d?.focus_areas) ? (d.focus_areas as string[]) : null,
        known_kid_colors:
          d?.known_kid_colors &&
          typeof d.known_kid_colors === "object" &&
          !Array.isArray(d.known_kid_colors)
            ? (d.known_kid_colors as Record<string, string>)
            : {},
        notifications_weekly_enabled: !!d?.notifications_weekly_enabled,
        notifications_weekly_day_of_week: d?.notifications_weekly_day_of_week ?? 0,
        notifications_weekly_time: d?.notifications_weekly_time || "18:00",
        notifications_morning_enabled: !!d?.notifications_morning_enabled,
        notifications_morning_time: d?.notifications_morning_time || "07:00",
        notifications_prompted_at: d?.notifications_prompted_at ?? null,
        notifications_timezone: d?.notifications_timezone ?? null,
        // null, not 'device' — an absent value means we do not know, and
        // guessing here would be the exact defect #555 is about.
        notifications_timezone_source:
          d?.notifications_timezone_source === "device" || d?.notifications_timezone_source === "user"
            ? d.notifications_timezone_source
            : null,
        my_kin_section_order: d?.my_kin_section_order ?? null,
        briefing_prefs: d?.briefing_prefs ?? null,
        phone_number: d?.phone_number ?? null,
        phone_verified_at: d?.phone_verified_at ?? null,
        sms_morning_enabled: !!d?.sms_morning_enabled,
        sms_task_updates_enabled: !!d?.sms_task_updates_enabled,
        sms_weekly_enabled: !!d?.sms_weekly_enabled,
        // ?? true, not !! — the column defaults true and a missing/undefined value
        // must not read as "push off" (see DEFAULT_PREFS).
        notifications_task_updates_enabled: d?.notifications_task_updates_enabled ?? true,
        // ⚠️ #109 — absent falls back to the TASK-UPDATES value, not to the
        // column default. These two columns are BACKFILLED from that pair, so
        // between this deploy and the migration the honest answer to "is the
        // Ready for you row on?" is "whatever Task updates says" — that is
        // literally what she is still receiving, and what the Edge Functions'
        // own fallback pair is reading in the same window. Defaulting to
        // true/false here instead would show her a row that does not match
        // either her past behavior or her next hour of it.
        notifications_ready_enabled:
          d?.notifications_ready_enabled ?? (d?.notifications_task_updates_enabled ?? true),
        sms_ready_enabled: d?.sms_ready_enabled ?? !!d?.sms_task_updates_enabled,
        sms_opted_out: !!d?.sms_opted_out,
        // ⚠️ `!== false`, not `!!`. Before the migration the key is ABSENT, and
        // absent must mean ON — `!!undefined` is false and would render the
        // toggle off for everyone until the SQL runs.
        sms_two_way_enabled: (d as { sms_two_way_enabled?: boolean })?.sms_two_way_enabled !== false,
        // ?? true, not !! — same reasoning as notifications_task_updates_enabled
        // above: the column defaults true, and a missing/undefined value must
        // not read as "autofill off" for an account whose row hasn't loaded yet.
        grocery_autofill_enabled: d?.grocery_autofill_enabled ?? true,
        // ?? false, not ?? true — a real cart mutation, deliberately the
        // opposite fail-open lean from grocery_autofill_enabled above.
        kroger_cart_autoload_enabled: d?.kroger_cart_autoload_enabled ?? false,
      });
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, authLoading, refetchKey]);

  // Backfill notifications_timezone once per session if it was never set.
  //
  // The dispatcher's who's-due RPCs require this column NOT NULL, and it has no
  // DB default. Until now it was only written as a side effect of changing a
  // preference, so a user who opted into texts during onboarding and never
  // opened Settings was invisible to the dispatcher — no morning note, no
  // weekly nudge, no clue why. See shouldBackfillTimezone for why this only
  // fills a blank and never overwrites.
  //
  // Fire-and-forget: a failure here must never block rendering or surface to
  // her. The ref keeps it to one attempt per mount, so a failed write doesn't
  // retry in a loop against every prefs change.
  const timezoneBackfilled = useRef(false);
  useEffect(() => {
    if (timezoneBackfilled.current) return;
    if (!shouldBackfillTimezone({
      loading,
      hasUser: !!user,
      storedTimezone: prefs.notifications_timezone,
    })) return;
    const timezone = resolveTimezone();
    if (!timezone) return;
    timezoneBackfilled.current = true;
    void (async () => {
      const { error } = await supabase
        .from("user_preferences")
        .upsert(
          {
            user_id: user!.id,
            notifications_timezone: timezone,
            // #555. This value came from Intl.DateTimeFormat() — the browser
            // clock — not from her. Recording that is the whole point: without
            // it a guess is indistinguishable from a preference forever after,
            // and the #554 audit already drew a wrong conclusion from exactly
            // that ambiguity.
            notifications_timezone_source: "device",
            updated_at: new Date().toISOString(),
          },
          { onConflict: "user_id" }
        );
      if (error) {
        console.warn("[UserPreferences] timezone backfill failed", error.message);
        return;
      }
      setPrefs((prev) => ({
        ...prev,
        notifications_timezone: timezone,
        notifications_timezone_source: "device",
      }));
    })();
  }, [loading, user, prefs.notifications_timezone]);

  const updatePreferences = useCallback(
    async (patch: Partial<UserPrefs>, { write = true }: { write?: boolean } = {}) => {
      setPrefs((prev) => ({ ...prev, ...patch }));
      if (!write || !user) return null;
      const { error } = await supabase
        .from("user_preferences")
        .upsert(
          { user_id: user.id, ...(patch as any), updated_at: new Date().toISOString() },
          { onConflict: "user_id" }
        );
      // Returned, not thrown: the optimistic setPrefs above already happened,
      // so a caller that doesn't care stays unchanged, while a caller that
      // shows a success receipt can check before it claims the save worked.
      //
      // ⚠️ THE COST OF THAT CHOICE, so the next reader doesn't rediscover it:
      // because this RESOLVES rather than throws, a `try/catch` around a call
      // to it CANNOT FIRE. Two sites in TodayTab had exactly that and were
      // silently unprotected for months while reading as handled (#545). If
      // you are handling a failure here, `const error = await …` is the only
      // form that works — never a catch.
      //
      // As of #545 PR 3 there are 19 call sites and 10 read this value.
      // The 9 that discard it are deliberate: they are optimistic writes with
      // nothing on screen claiming success, so there is no false statement to
      // suppress. Adding a confirmation to any of them makes checking this
      // return value mandatory, not optional — that is the whole lesson of
      // #537's Morning Notes fix becoming a #545 defect.
      return error;
    },
    [user]
  );

  const refetchPreferences = useCallback(() => {
    setRefetchKey((k) => k + 1);
  }, []);

  return (
    <UserPreferencesContext.Provider value={{ prefs, loading, updatePreferences, refetchPreferences }}>
      {children}
    </UserPreferencesContext.Provider>
  );
};

export const usePreferences = () => useContext(UserPreferencesContext);
